import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { Select } from "antd/es";
import { DefaultOptionType, SelectValue } from "antd/lib/select";
import debounce from "lodash.debounce";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import Icon from "../../assets/icons/icon";
import StatusSelect from "../../components/form/statusSelect";
import { renderErrorMessage } from "../../components/messages/errorMessage";
import ServerSelectBox from "../../components/serverSelectBox";
import StatusTabs from "../../components/statusTabs";
import StatusTag from "../../components/statusTag";
import DataTable from "../../components/table/dataTable";
import { CustomColumnsType, RenderFunc } from "../../components/table/dataTableTypes";
import UserStore from "../../contexts/userStore";
import useSystemCaseSavedEventListener from "../../hooks/useSystemCaseSavedEventListener";
import _t from "../../lang/translate";
import PageTitle from "../../layout/pageTitle";
import { getPath, urlExtraParams } from "../../routes/appRoutes";
import { usersAutoComplete } from "../../services/autocompleteService";
import {
  deleteTaxLegalCase,
  initializeExport,
  taxLegalCasesURL,
} from "../../services/taxLegalService";
import {
  caseHandlerKey,
  obsoletedKey,
  pageSizeKey,
  receivableStatusKey,
  repairRequestedKey,
  reporterCompanyKey,
  searchKey,
  statusKey
} from "../../services/urlQueryService";
import { useApi } from "../../services/useApi";
import { PaginationedData } from "../../types/apiTypes";
import { IdType, Person, ScreenProps } from "../../types/appTypes";
import { TaxLegalDashboard } from "../../types/taxLegalTypes";
import consts from "../../utilities/consts";
import format from "../../utilities/formatNumbers";
import { getRandomString } from "../../utilities/stringExtraFunctions";

const reporterCompanyFilters = [
  { id: null, title: "Alle" },
  { id: "dmf", title: "DMF" },
  { id: "not-dmf", title: "Forhandlere" },
]

const receivableStatusFilterOptions: DefaultOptionType[] = [
  { label: "Tilbud Oprettet", value: "created" },
  { label: "Tilbud Sendt", value: "published" },
  { label: "Tilbud Accepteret", value: "accepted" },
  { label: "Faktura Sendt", value: "invoiceSent" },
  { label: "Faktura Betalt", value: "paid" },
]

const TaxLegal: FC<ScreenProps> = ({ title }) => {
  const { hasPermission } = UserStore.useContainer();
  const [doRefund, setDoRefund] = useState<boolean>(false);
  const [{ data, isLoading, isError }, setUrl, setData] = useApi<
    PaginationedData<TaxLegalDashboard>
  >("", { data: [] }, true);
  const location = useLocation();
  const history = useHistory()

  const delayedSetUrl = useCallback(
    debounce((url: string) => {
      setUrl(url);
    }, 350),
    [setUrl],
  );

  useSystemCaseSavedEventListener((event) => {
    const { id, context, data: taxLegalListItem } = event?.detail
    if (!id) return;
    if (context !== 'taxLegal') return;
    setData((prev) => ({
      ...prev,
      data: prev.data.map(item => item.id === id ? taxLegalListItem as TaxLegalDashboard : item)
    }))
  })


  const activeReporterCompanyFilter = useMemo(() => {
    const searchValue = new URLSearchParams(location.search).get(reporterCompanyKey) ?? reporterCompanyFilters[0].id;
    return searchValue
  }, [location.search])

  function setActiveReporterCompanyFilter(value: string) {
    const query = new URLSearchParams(location.search);
    if (!value || value === 'null') query.delete(reporterCompanyKey);
    else query.set(reporterCompanyKey, value.toString());

    history.push({
      ...history.location,
      pathname: location.pathname,
      search: query.toString(),
    });
  }

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    query.set(pageSizeKey, localStorage.getItem(pageSizeKey) || "20");
    const url = taxLegalCasesURL(query.toString());
    query.has(searchKey) ? delayedSetUrl(url) : setUrl(url);
  }, [setUrl, location.search, delayedSetUrl]);

  function handleRefresh() {
    const query = new URLSearchParams(location.search);
    query.set(pageSizeKey, localStorage.getItem(pageSizeKey) || "20");
    query.set(getRandomString(), new Date().getSeconds().toString());
    const url = taxLegalCasesURL(query.toString());
    setUrl(url);
  }

  const columns: CustomColumnsType<TaxLegalDashboard> = [
    {
      title: _t("case", "number_short"),
      align: "center",
      key: "id",
      sorter: true,
      dataIndex: "id",
      width: 110,
      render: (text, record) =>
        hasPermission("screen.tax") ? (
          <>
            {record.repairRequestedAt && (
              <Typography.Text type={"warning"} className="priority-icon-table">
                <Tooltip
                  title={
                    "Reparation påkrævet (markeret d. " +
                    format.date(record.repairRequestedAt) +
                    ")"
                  }
                >
                  <Icon name="build-outline" size="large" style={{ display: "block" }} />
                </Tooltip>
              </Typography.Text>
            )}
            <Link to={getPath("taxLegal", record.id)}>
              <strong>{text}</strong>
            </Link>
          </>
        ) : (
          text
        ),
      fixed: "left",
    },
    {
      title: _t("date"),
      key: "createdAt",
      sorter: true,
      dataIndex: "createdAt",
      width: 110,
      render: (date) => {
        return format.date(date);
      },
    },
    {
      title: _t("dealer"),
      key: "dealerName",
      dataIndex: "dealerName",
      width: 155,
      ellipsis: true,
      // sorter: true,
    },
    {
      title: _t("caseHandler"),
      key: "caseHandler",
      dataIndex: "caseHandler",
      width: 155,
      ellipsis: true,
      render: (caseHandler: Person | null) => {
        return caseHandler?.fullName;
      },
    },
    {
      title: _t("ms.caseNumber"),
      key: "governmentCaseNumber",
      sorter: true,
      dataIndex: "governmentCaseNumber",
      width: 140,
      ellipsis: true,
      render: (governmentCaseNumber: string) => {
        const governmentCaseIds = governmentCaseNumber?.split(',')
          .filter(Boolean);
        if (governmentCaseIds && governmentCaseIds?.length > 1) {
          const [first, ...rest] = governmentCaseIds
          return (
            <div>
              <span className="mr-05">{first}</span>
              <Popover title={"Journal numre"} content={
                rest.map(id => (
                  <div key={id}>{id}</div>
                ))
              }>
                <Tag className="ant-tag-exception">+{rest.length}</Tag>
              </Popover>
            </div>
          )
        }

        return (
          <span>{governmentCaseIds?.[0] ?? ""}</span>
        )

      },
    },
    {
      title: _t("ms.groupCaseNumber"),
      key: "governmentBatchId",
      dataIndex: "governmentBatchId",
      width: 160,
    },
    {
      title: _t("reviewer"),
      key: "reporterCompany",
      dataIndex: "reporterCompany",
    },
    {
      title: _t("customer"),
      key: "clientName",
      dataIndex: "clientName",
      width: 125,
      ellipsis: true,
    },
    {
      title: _t("vehicle"),
      key: "car",
      dataIndex: "car",
      ellipsis: true,
    },
    {
      title: _t("vin"),
      key: "vin",
      dataIndex: "vin",
      width: 165,
    },
    {
      title: _t("firstRegDate"),
      key: "firstRegDate",
      dataIndex: "firstRegDate",
      width: 165,
      render: (date) => format.date(date),
    },
    {
      title: _t("status"),
      key: "status",
      sorter: true,
      dataIndex: "status",
      width: 200,
      render: (status) => <StatusTag status={status} size="small" />,
    },
    {
      title: _t("deadline", "date_at"),
      key: "nextDeadline",
      dataIndex: "nextDeadline",
      sorter: true,
      width: 142,
      render: (date) => format.date(date),
    },
    {
      title: _t("expiration_date"),
      key: "statuteOfLimitationsDate",
      sorter: true,
      dataIndex: "statuteOfLimitationsDate",
      width: 142,
      render: (date) => format.date(date),
    },
    {
      title: _t("archived", "date_at"),
      key: "obsoletedAt",
      dataIndex: "obsoletedAt",
      width: 142,
      render: (date) => format.date(date),
    },
    {
      title: _t("outcome"),
      key: "penaltyType",
      dataIndex: "penaltyType",
      width: 142,
      render: (penaltyType) => <StatusTag status={penaltyType} size="small" />,
    },
    {
      title: _t("regulation"),
      key: "regulationAmount",
      dataIndex: "regulationAmount",
      width: 142,
      render: (regulationAmount) => {
        return format.price(Number(regulationAmount));
      },
    },
    {
      title: _t("economy"),
      key: "receivables",
      dataIndex: "receivables",
      width: 142,
      render: (receivables: TaxLegalDashboard['receivables']) => {
        if (!receivables?.length) {
          return <Tag className="ant-tag-exception">0 {_t("offer")}</Tag>
        }
        return (
          <Popover title={_t("economy")}
            content={<div style={{ minWidth: 448 }}>
              {receivables.map(item => {
                return (
                  <Row key={item.id} className="w-100 mb-03" gutter={[8, 8]} align="middle">
                    <Col flex={'120px'}>
                      <StatusTag size="small" status={item.status} />
                    </Col>
                    <Col flex={"1 1 0%"}><span className="text-ellipsis">{item.title}</span></Col>
                    <Col flex={"0 0 auto"}>{format.price(item.totals?.withVat)}</Col>
                  </Row>
                )
              })}
            </div>}
          >
            <Tag color="processing" className="ant-tag-exception">{receivables.length} {_t("offer")}</Tag>
          </Popover>
        )
      },
    },


  ];

  const handleDelete = async (id: IdType) => {
    const originalData = { ...data };
    try {
      await deleteTaxLegalCase(id, doRefund);
      message.success("#" + id + " " + _t("deleted"));
      setData((state) => ({
        ...state,
        data: originalData.data.filter((item) => item.id !== id),
      }));
    } catch (error) {
      renderErrorMessage(error, _t("msg.not_deleted"));
    }
  };

  const tableActions: RenderFunc<TaxLegalDashboard> = (_, { id }) => {
    return (
      <Popconfirm
        placement="topLeft"
        onVisibleChange={() => setDoRefund(false)}
        onConfirm={() => handleDelete(id)}
        icon={<Icon fill="red" name="information-circle-outline" />}
        title={
          <div>
            {_t("msg.confirm_delete")}&nbsp;
            <strong>{id}</strong>
            <div>
              <Checkbox checked={doRefund} onChange={(e) => setDoRefund(e.target.checked)}>
                {_t("refund", "credits")}
              </Checkbox>
            </div>
          </div>
        }
      >
        <Button
          className="muted delete-btn"
          type="text"
          shape="circle"
          icon={<Icon name="trash-outline" />}
        />
      </Popconfirm>
    );
  };


  return (
    <>
      <PageTitle
        className="fluid-page-title"
        title={title}
      >
        <Row justify="space-between" align="middle">
          <Col flex={1}>
            <StatusTabs
              statuses={reporterCompanyFilters}
              onTabChange={(key) => setActiveReporterCompanyFilter(key)}
              activeTabId={`${activeReporterCompanyFilter}`}
            />
          </Col>
          <Col>
            <Button type="primary">
              <Link to={getPath("taxLegal", urlExtraParams.new)}>+ {_t("create", "case")}</Link>
            </Button>
          </Col>
        </Row>
      </PageTitle>
      <DataTable<TaxLegalDashboard>
        onRefresh={handleRefresh}
        filter={<TableFilters />} // TODO
        loading={isLoading}
        renderActions={tableActions}
        columnStorageKey="TAXLEGAL"
        columns={columns}
        dataSource={data.data}
        meta={data.meta}
      />
    </>
  );
};

export default TaxLegal;

function TableFilters() {
  const location = useLocation();
  const history = useHistory();
  const { user } = UserStore.useContainer();
  const searchParams = new URLSearchParams(location.search);
  const initStatusFilter = searchParams.get(statusKey);
  const [caseHandlerFilter, setCaseHandlerFilter] = useState(
    searchParams.get(caseHandlerKey) ? Number(searchParams.get(caseHandlerKey)) : undefined,
  );

  const receivableStatusFilter = useMemo(() => {
    return new URLSearchParams(location.search).get(receivableStatusKey)
  }, [location])

  const [exportOpen, setExportOpen] = useState(false);
  const [repairRequestedFilter, setRepairRequestedFilter] = useState<boolean | null>(
    searchParams.get(repairRequestedKey) === "true"
      ? true
      : searchParams.get(repairRequestedKey) === "false"
        ? false
        : null,
  );

  const [obsoletedAtFilter, setObsoletedAtFilter] = useState<boolean | null>(
    searchParams.get(obsoletedKey) === "true"
      ? true
      : searchParams.get(obsoletedKey) === "false"
        ? false
        : null,
  );
  const repairRequestedFilterColor =
    repairRequestedFilter === true
      ? "warning"
      : repairRequestedFilter === false
        ? "success"
        : "secondary";

  const obsoletedColor =
    obsoletedAtFilter === true
      ? "warning"
      : obsoletedAtFilter === false
        ? "success"
        : "secondary";


  const onStatusFilterChange = (id: SelectValue) => {
    const query = new URLSearchParams(location.search);
    if (!id) query.delete(statusKey);
    else query.set(statusKey, id.toString());

    changeHistory(query);
  };

  const onUserFilterChange = (id: SelectValue) => {
    const query = new URLSearchParams(location.search);
    if (!id) {
      setCaseHandlerFilter(undefined);
      query.delete(caseHandlerKey);
    } else {
      setCaseHandlerFilter(Number(id));
      query.set(caseHandlerKey, id.toString());
    }

    changeHistory(query);
  };


  const onFilterForRepairRequested = () => {
    const query = new URLSearchParams(location.search);

    if (repairRequestedFilter === true) {
      setRepairRequestedFilter(false);
      query.set(repairRequestedKey, "false");
    } else if (repairRequestedFilter === false) {
      setRepairRequestedFilter(null);
      query.delete(repairRequestedKey);
    } else {
      setRepairRequestedFilter(true);
      query.set(repairRequestedKey, "true");
    }

    changeHistory(query);
  };

  function updateReceivableStatusFilter(value: string) {
    const query = new URLSearchParams(location.search)
    if (value) query.set(receivableStatusKey, value.toString())
    else query.delete(receivableStatusKey)
    changeHistory(query)
  }

  const onHideExpiredFilter = () => {
    const query = new URLSearchParams(location.search);

    if (obsoletedAtFilter === null) {
      setObsoletedAtFilter(false);
      query.set(obsoletedKey, "false");
    }
    else if (obsoletedAtFilter === false) {
      setObsoletedAtFilter(true);
      query.set(obsoletedKey, "true");
    }
    else {
      setObsoletedAtFilter(null);
      query.delete(obsoletedKey);
    }


    changeHistory(query);
  };

  function changeHistory(query: URLSearchParams) {
    history.push({
      ...history.location,
      pathname: location.pathname,
      search: query.toString(),
    });
  }


  return (
    <>
      <Space align="center">
        <Typography.Text type={repairRequestedFilterColor}>
          <Tooltip title={"Vis kun sager markeret med 'Reparation påkrævet'"}>
            <Icon
              name="build-outline"
              size="large"
              onClick={() => onFilterForRepairRequested()}
              style={{ display: "block" }}
            />
          </Tooltip>
        </Typography.Text>

        <Typography.Text type={obsoletedColor}>
          <Tooltip title={"Vis/skjul sager som er udløbet"}>
            <Icon
              name="time-outline"
              size="large"
              onClick={() => onHideExpiredFilter()}
              style={{ display: "block" }}
            />
          </Tooltip>
        </Typography.Text>

        <Select style={{ width: 160 }}
          value={receivableStatusFilter}
          onChange={updateReceivableStatusFilter}
          placeholder={_t("economy")}
          allowClear
          options={receivableStatusFilterOptions}
        />

        {user?.dealer.id && (
          <ServerSelectBox
            allowClear
            style={{ width: 210 }}
            value={caseHandlerFilter}
            onChange={onUserFilterChange}
            apiUrl={usersAutoComplete()}
            placeholder={_t("choose", "caseHandler")}
            filters={`filter[dealerId]=${user.dealer.id}`}
          />
        )}

        <StatusSelect
          defaultValue={initStatusFilter !== null ? Number(initStatusFilter) : undefined}
          showSearch={false}
          className="width-180"
          placeholder={_t("status", "filter")}
          allowClear
          onChange={(value) => onStatusFilterChange(value)}
          contexts={"taxLegal"}
        />

        <Button onClick={() => setExportOpen(true)}>{_t("export")}</Button>
      </Space>

      <ExportModal open={exportOpen} setOpen={setExportOpen} />
    </>
  );
}

function ExportModal({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
  const [exportForm] = Form.useForm<{ email: string }>();
  const [loading, setLoading] = useState(false);
  async function onExport() {
    try {
      setLoading(true);
      const vals = exportForm.getFieldsValue();
      const { data } = await initializeExport({ to: vals.email });
      if (data.data) {
        message.success(
          `${_t("export.toEmail.firstPart")} ${vals.email}. ${_t("export.toEmail.lastPart")}`,
        );
        exportForm.resetFields();
      } else {
        throw new Error(_t("exportFailedOnTheServer"));
      }
    } catch (error) {
      renderErrorMessage(error);
    } finally {
      setOpen(false);
      setLoading(false);
    }
  }
  return (
    <Modal
      visible={open}
      okButtonProps={{ loading: loading }}
      cancelButtonProps={{ loading: loading }}
      onOk={onExport}
      onCancel={() => setOpen(false)}
      title={_t("exportList")}
    >
      <Form form={exportForm} {...consts.formItemProps}>
        <Form.Item name="email" label={_t("email")}>
          <Input disabled={loading} type="email" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
